import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BlogPostShell, POSTS } from "@/pages/Blog";

const POST = POSTS.find((p) => p.slug === "competition-secretary-field-manual");
const OG = "https://cameraclubscore.com/og/blog-generic.png";
const URL = "https://cameraclubscore.com/blog/competition-secretary-field-manual";

export default function PostSecretary() {
  return (
    <>
      <Helmet>
        <title>{`${POST.title} — Camera Club Score`}</title>
        <meta name="description" content={POST.excerpt} />
        <link rel="canonical" href={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={URL} />
        <meta property="og:title" content={POST.title} />
        <meta property="og:description" content={POST.excerpt} />
        <meta property="og:image" content={OG} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG} />
      </Helmet>
      <BlogPostShell post={POST}>
        <p className="lead">
          If you've just been handed the Competition Secretary role — first,
          welcome. Second, don't panic. It's a bigger job than "record the
          scores" but a smaller one than it feels in month one. Here's the
          field manual we wish someone had given us.
        </p>

        <h2>What does a Competition Secretary actually do?</h2>
        <p>
          Three things, in rough order of time: <strong>organise entries
          before judging night</strong>, <strong>record scores during</strong>,
          and <strong>publish results after</strong>. Everything else — chasing
          themes, briefing judges, printing certificates — is a variation
          on those.
        </p>

        <h2>How do I organise entries before judging night?</h2>
        <p>
          Two weeks out, remind members to submit. One week out, close
          entries. On the night, have a locked running order printed and
          ready. In Camera Club Score, this is one click: <strong>Add all
          photographers</strong> to a competition, review, then hit{" "}
          <strong>Lock order</strong>.
        </p>

        <h2>How do I record image titles?</h2>
        <p>
          The old way: type them into a spreadsheet the moment they arrive
          in your inbox. The new way: get members to submit through a form
          (CSV import, or a shared spreadsheet you import). Never rely on
          typing titles live during judging — you'll misspell every third
          one.
        </p>

        <h2>How do I record photographers' names?</h2>
        <p>
          Every photographer gets a short unique <strong>code</strong> (e.g.
          <em> JS01, MR03</em>). Codes never change; they follow the member
          season to season. This is what judges see; the code maps to a
          full name on your records.
        </p>

        <h2>What's the easiest way to manage competition entries?</h2>
        <p>
          The most common workflows in order of pain:
        </p>
        <ol>
          <li><strong>Paper slips</strong> — reliable but the running-order transcription is agony.</li>
          <li><strong>Email + spreadsheet</strong> — works, but breaks the moment your treasurer retires.</li>
          <li><strong>Shared Google Sheet</strong> — better; still fragile at judging time.</li>
          <li><strong>Purpose-built app</strong> — one place, everyone sees the same view, exports on demand.</li>
        </ol>

        <h2>How do I produce a judging score sheet?</h2>
        <p>
          If you're doing this in Word, stop. Every competition tool worth
          the name will generate a printable sheet with the running order,
          codes, image titles and columns for the judge's marks — in one
          click. <Link to="/">Camera Club Score</Link> ships this as
          standard on the Results page.
        </p>

        <h2>How do I create a running league table?</h2>
        <p>
          Add up the season's scores per photographer, weighted by your
          club's scoring mode (all scores, best-N, PoY points). The trick
          is doing this in a way that doesn't force you to rebuild the
          spreadsheet every year. See the pillar on
          <Link to="/blog/season-league-tables-and-photographer-of-the-year">
            {" "}season league tables and Photographer of the Year
          </Link>.
        </p>

        <h2>How do I record first, second and third place?</h2>
        <p>
          Once the judge picks their placements, enter them in the placement
          column alongside the score. If two images tie on score but only
          one gets a placement, the placement is the tiebreak — record it
          faithfully.
        </p>

        <h2>How do I manage several competition classes?</h2>
        <p>
          Create separate competitions for each class (e.g. "Nature —
          Beginner" and "Nature — Advanced"). Each has its own scoring,
          its own placement, its own league. Same judge can score both
          in one sitting.
        </p>

        <h2>How do I record highly commended images?</h2>
        <p>
          Use the placement field with values like <em>1st, 2nd, 3rd,
          HC1, HC2</em> — or however your club prints its certificates.
          Camera Club Score exports these to the Certificates CSV at
          season end so mail-merge just works.
        </p>

        <h2>How do I keep records from previous seasons?</h2>
        <p>
          Seasons in Camera Club Score never delete. Any admin can browse
          a five-year-old season, export it to CSV, or print a PDF report.
          Members love this — their whole photographic journey is one
          click away.
        </p>
      </BlogPostShell>
    </>
  );
}
